import { useCallback, useMemo } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import { formatDate } from 'src/utils/formatTime';

const HEADER_SX = { fontSize: '0.7rem', fontWeight: 600, py: 0.75 };
const CELL_SX = { fontSize: '0.7rem', py: 0.75, lineHeight: 1.3, wordBreak: 'break-all' };

// A column that draws more than its own value, such as a subtext beneath it, renders the cell itself.
const cellValue = (row, column) => {
  if (column.render) {
    return column.render(row);
  }

  const value = row[column.key];

  if (value === null || value === undefined || value === '') {
    return '--';
  }

  return column.date ? formatDate(value) : value;
};

// One detail section, drawn from a query the caller supplies. The sections differ only in which
// endpoint they read and which columns they draw, so callers own the hook and this owns everything
// else. An infinite query is read page by page; a plain one returns its rows whole.
const SectionTable = ({ section, query, enabled = true }) => {
  const rows = useMemo(
    () =>
      query.data?.pages
        ? query.data.pages.flatMap(page => page[section.itemsKey] || [])
        : query.data || [],
    [query.data, section.itemsKey],
  );

  const loadMore = useCallback(() => {
    if (query.hasNextPage) {
      query.fetchNextPage();
    }
  }, [query]);

  const [sentryRef] = useInfiniteScroll({
    loading: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    disabled: !enabled,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 240px 0px',
  });

  return (
    <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
      <TableHead sx={{ backgroundColor: '#3f3f3f' }}>
        <TableRow>
          {section.columns.map(column => (
            <TableCell key={column.key} sx={{ ...HEADER_SX, width: column.width }}>
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
        {query.isLoading ? (
          <LoadingRows columnCount={section.columns.length} rowCount={4} />
        ) : (
          <>
            {rows.map((row, index) => (
              <TableRow
                key={`${section.key}-${index}`}
                ref={index === rows.length - 1 ? sentryRef : null}>
                {section.columns.map(column => (
                  <TableCell key={column.key} sx={CELL_SX}>
                    {cellValue(row, column)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {query.isFetchingNextPage && (
              <LoadingRows columnCount={section.columns.length} rowCount={2} />
            )}
            {!query.isFetching && rows.length === 0 && (
              <NoDataCell colSpan={section.columns.length}>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>
                  {section.emptyMessage}
                </Typography>
              </NoDataCell>
            )}
          </>
        )}
      </TableBody>
    </Table>
  );
};

export default SectionTable;
